import { z } from 'zod';

const SK_PROVINCE_CODE = 'SK';

export const MB_SERVICE_LOCATION_INDICATOR_CODES = ['C', 'O', 'E'] as const;
export const SK_SERVICE_LOCATION_INDICATOR_CODES = ['R', 'S', 'X'] as const;

export const MB_INCISION_CODES = ['B', 'D', 'S'] as const;
export const SK_BILATERAL_INDICATOR_CODES = ['L', 'R', 'B'] as const;

export const MB_SPLIT_CODES = [
  'A',
  'C',
  'D',
  'E',
  'G',
  'H',
  'K',
  'L',
  'N',
  'P',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
] as const;
export const SK_SPECIAL_CIRCUMSTANCES_CODES = [
  'TF',
  'PF',
  'CF',
  'TA',
  'VT',
] as const;
export const SK_CLAIM_TYPE_CODES = ['P', 'W', 'D'] as const;
export const SK_CLAIM_MODE_CODES = ['0', '1', '6', '9'] as const;
export const SK_FORM_TYPE_CODES = ['8', 'E'] as const;
export const SK_CORPORATION_INDICATOR_CODES = ['A', 'B', 'C', 'D'] as const;

type ProvinceClaimFieldData = {
  payor?: { province?: string | null } | null;
  serviceLocationIndicator?: string | null;
  claimType?: string | null;
  claimMode?: string | null;
  formType?: string | null;
  corporationIndicator?: string | null;
  specialCircumstances?: string | null;
  tariffs?: Array<{
    incision?: string | null;
    split?: string | null;
  }>;
};

const normalizeCode = (value: string | null | undefined): string =>
  value?.trim().toUpperCase() ?? '';

const getProvince = (data: ProvinceClaimFieldData): string =>
  normalizeCode(data.payor?.province);

const includesCode = (
  codes: readonly string[],
  value: string | null | undefined
): boolean => {
  const code = normalizeCode(value);
  return !code || codes.includes(code);
};

export const isSKClaim = (data: ProvinceClaimFieldData): boolean =>
  getProvince(data) === SK_PROVINCE_CODE;

export const requiresHospitalDetails = (
  data: ProvinceClaimFieldData
): boolean =>
  !isSKClaim(data) && Boolean(data.serviceLocationIndicator?.trim());

export const validateProvinceClaimFields = (
  data: ProvinceClaimFieldData,
  ctx: z.RefinementCtx
): void => {
  const province = getProvince(data);
  if (!province) {
    return;
  }

  const isSK = province === SK_PROVINCE_CODE;
  const serviceLocationCodes = isSK
    ? SK_SERVICE_LOCATION_INDICATOR_CODES
    : MB_SERVICE_LOCATION_INDICATOR_CODES;
  const incisionCodes = isSK ? SK_BILATERAL_INDICATOR_CODES : MB_INCISION_CODES;
  const splitCodes = isSK ? [] : MB_SPLIT_CODES;

  if (!includesCode(serviceLocationCodes, data.serviceLocationIndicator)) {
    ctx.addIssue({
      code: 'custom',
      message: `Service Location Indicator is not valid for ${province} claims`,
      path: ['serviceLocationIndicator'],
    });
  }

  const skClaimFields = [
    {
      value: data.claimType,
      codes: SK_CLAIM_TYPE_CODES,
      label: 'Claim Type',
      path: 'claimType',
    },
    {
      value: data.claimMode,
      codes: SK_CLAIM_MODE_CODES,
      label: 'Claim Mode',
      path: 'claimMode',
    },
    {
      value: data.formType,
      codes: SK_FORM_TYPE_CODES,
      label: 'Form Type',
      path: 'formType',
    },
    {
      value: data.corporationIndicator,
      codes: SK_CORPORATION_INDICATOR_CODES,
      label: 'Corporation Indicator',
      path: 'corporationIndicator',
    },
    {
      value: data.specialCircumstances,
      codes: SK_SPECIAL_CIRCUMSTANCES_CODES,
      label: 'Special Circumstances',
      path: 'specialCircumstances',
    },
  ] as const;

  skClaimFields.forEach(({ value, codes, label, path }) => {
    const valid = isSK ? includesCode(codes, value) : !normalizeCode(value);
    if (!valid) {
      ctx.addIssue({
        code: 'custom',
        message: isSK
          ? `${label} is not valid for SK claims`
          : `${label} is only valid for SK claims`,
        path: [path],
      });
    }
  });

  data.tariffs?.forEach((tariff, index) => {
    if (!includesCode(incisionCodes, tariff.incision)) {
      ctx.addIssue({
        code: 'custom',
        message: `Bilateral/Incision code is not valid for ${province} claims`,
        path: ['tariffs', index, 'incision'],
      });
    }

    if (!includesCode(splitCodes, tariff.split)) {
      ctx.addIssue({
        code: 'custom',
        message: `Split code is not valid for ${province} claims`,
        path: ['tariffs', index, 'split'],
      });
    }
  });
};
